/** AI chat search. Port of flock-expo/src/hooks/useChatSearch.ts (AsyncStorage → localStorage). */
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/lib/apiClient';
import type { ChatSearchResponse } from '@flock-types/chat.types';

const MAX_RECENT_SEARCHES = 10;

function getSearchHistoryKey(groupId: string): string {
  return `chat_search_history_${groupId}`;
}

export function useChatSearch(groupId: string, chatGroupId?: string | null) {
  const [result, setResult] = useState<ChatSearchResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    if (groupId) {
      try {
        const stored = localStorage.getItem(getSearchHistoryKey(groupId));
        if (stored) {
          const parsed: { query: string; timestamp: number }[] = JSON.parse(stored);
          setRecentSearches(parsed.map((s) => s.query));
        }
      } catch {
        // Ignore storage errors
      }
    }
    return () => {
      mountedRef.current = false;
    };
  }, [groupId]);

  const saveRecentSearch = useCallback(
    (query: string) => {
      try {
        const key = getSearchHistoryKey(groupId);
        const stored = localStorage.getItem(key);
        let history: { query: string; timestamp: number }[] = stored ? JSON.parse(stored) : [];
        history = history.filter((s) => s.query.toLowerCase() !== query.toLowerCase());
        history.unshift({ query, timestamp: Date.now() });
        history = history.slice(0, MAX_RECENT_SEARCHES);
        localStorage.setItem(key, JSON.stringify(history));
        if (mountedRef.current) setRecentSearches(history.map((s) => s.query));
      } catch {
        // Ignore storage errors
      }
    },
    [groupId]
  );

  const search = useCallback(
    async (query: string) => {
      if (!query.trim() || !groupId) return;
      setLoading(true);
      setError(null);
      setResult(null);
      try {
        const body: { query: string; chatGroupId?: string } = { query: query.trim() };
        if (chatGroupId) body.chatGroupId = chatGroupId;

        const response = await api.post<{ success: boolean; data: ChatSearchResponse }>(
          `/groups/${groupId}/chat/search`,
          body
        );

        if (!mountedRef.current) return;
        if (response.success && response.data) {
          setResult(response.data);
          saveRecentSearch(query.trim());
        } else {
          setError('No results found. Try describing what you remember differently.');
        }
      } catch (err: any) {
        if (!mountedRef.current) return;
        if (err?.response?.status === 429) {
          setError('Please wait a moment before searching again.');
        } else {
          setError('Something went wrong. Please try again.');
        }
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    },
    [groupId, chatGroupId, saveRecentSearch]
  );

  const clear = useCallback(() => {
    setResult(null);
    setError(null);
    setLoading(false);
  }, []);

  const clearRecentSearches = useCallback(() => {
    try {
      localStorage.removeItem(getSearchHistoryKey(groupId));
      setRecentSearches([]);
    } catch {
      // Ignore storage errors
    }
  }, [groupId]);

  return { search, result, loading, error, clear, recentSearches, clearRecentSearches };
}

export default useChatSearch;
